import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, MapPin, Users, Timer, AlertTriangle, Trophy, Star, Shield } from 'lucide-react';
import { Game } from '@/services/gamesService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import { format, differenceInMilliseconds } from 'date-fns';
import { TeamLogo } from './TeamLogo';
import { ScorekeeperApplicationModal } from './ScorekeeperApplicationModal';
import { PlayerJoinRequestButton } from './PlayerJoinRequestButton';

interface EnhancedGameDetailsModalProps {
  game: Game | null;
  isOpen: boolean;
  onClose: () => void;
  onGameUpdate?: () => void;
}

interface GameParticipant {
  id: string;
  player_id: string;
  team_side: string;
  participation_status: string;
  eligibility_status: string;
  position?: string;
  player_type: string;
  player_name?: string;
  confirmed_by?: string;
  profiles?: {
    first_name?: string;
    last_name?: string;
    username?: string;
  };
  internal_users?: {
    username?: string;
  };
}

interface VenueSection {
  id: string;
  section_name: string;
  section_type: string;
  capacity?: number;
}

interface ScorekeeperApplication {
  id: string;
  scorekeeper_id: string;
  status: string;
  applied_at: string;
  backup_position: number;
  scorekeeper_status: string;
  profiles?: {
    first_name?: string;
    last_name?: string;
    username?: string;
  };
}

export const EnhancedGameDetailsModal: React.FC<EnhancedGameDetailsModalProps> = ({
  game,
  isOpen,
  onClose,
  onGameUpdate
}) => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;

  const [participants, setParticipants] = useState<GameParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isCoachOrOwner, setIsCoachOrOwner] = useState(false);
  const [venueSection, setVenueSection] = useState<VenueSection | null>(null);
  const [scorekeeperApplications, setScorekeeperApplications] = useState<ScorekeeperApplication[]>([]);
  const [showScorekeeperModal, setShowScorekeeperModal] = useState(false);
  const [teamLogos, setTeamLogos] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    if (game && isOpen) {
      loadGameData();
      checkUserPermissions();
      loadVenueSection();
      loadScorekeeperApplications();
      loadTeamLogos();
      
      if (game.countdown_end_at) {
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
      }
    }
  }, [game, isOpen, user]);

  const updateCountdown = () => {
    if (!game?.countdown_end_at) return;
    
    const now = new Date();
    const endTime = new Date(game.countdown_end_at);
    const secondsLeft = Math.floor(differenceInMilliseconds(endTime, now) / 1000);
    
    if (secondsLeft <= 0) {
      setTimeRemaining('LOCKED');
      return;
    }
    
    const hours = Math.floor(secondsLeft / 3600);
    const minutes = Math.floor((secondsLeft % 3600) / 60);
    const seconds = secondsLeft % 60;
    
    setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
  };

  const loadGameData = async () => {
    if (!game) return;
    
    setLoading(true);
    try {
      // Load participants first without problematic joins
      const { data: participantsData, error: participantsError } = await supabase
        .from('game_participants')
        .select('*')
        .eq('game_id', game.id);

      if (participantsError) throw participantsError;

      // Fetch user data separately based on player_type
      const enrichedParticipants = await Promise.all(
        (participantsData || []).map(async (participant) => {
          let userInfo = null;
          
          if (participant.player_type === 'internal') {
            const { data: internalUserData } = await supabase
              .from('internal_users')
              .select('username')
              .eq('id', participant.player_id)
              .maybeSingle();
            userInfo = internalUserData;
          } else {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('first_name, last_name, username')
              .eq('id', participant.player_id)
              .maybeSingle();
            userInfo = profileData;
          }
          
          return {
            ...participant,
            profiles: participant.player_type === 'supabase' ? userInfo : null,
            internal_users: participant.player_type === 'internal' ? userInfo : null
          };
        })
      );
      
      // Helper function to get player display name
      const getPlayerDisplayName = (participant: any) => {
        if (participant.player_type === 'internal') {
          return participant.internal_users?.username || 'Internal User';
        } else {
          // For Supabase users, prioritize username, then first_name last_name
          const profile = participant.profiles;
          if (profile?.username) {
            return profile.username;
          }
          if (profile?.first_name || profile?.last_name) {
            return `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim();
          }
          return 'User';
        }
      };
      
      setParticipants(enrichedParticipants.map(p => ({
        ...p,
        player_name: getPlayerDisplayName(p)
      })) as GameParticipant[]);
    } catch (error) {
      console.error('Error loading game data:', error);
      toast.error('Failed to load game details');
    } finally {
      setLoading(false);
    }
  };

  const loadVenueSection = async () => {
    if (!game) return;
    
    try {
      // Check if this is a venue game with section info
      const { data: venueGameData } = await supabase
        .from('venue_games')
        .select(`
          venue_section_id,
          venue_sections!inner (
            id,
            section_name,
            section_type,
            seating_capacity,
            standing_capacity
          )
        `)
        .eq('id', game.id)
        .maybeSingle();

      if (venueGameData?.venue_sections) {
        setVenueSection({
          id: venueGameData.venue_sections.id,
          section_name: venueGameData.venue_sections.section_name,
          section_type: venueGameData.venue_sections.section_type,
          capacity: (venueGameData.venue_sections.seating_capacity || 0) + 
                   (venueGameData.venue_sections.standing_capacity || 0)
        });
      }
    } catch (error) {
      console.error('Error loading venue section:', error);
    }
  };

  const loadScorekeeperApplications = async () => {
    if (!game) return;
    
    try {
      const { data, error } = await supabase
        .from('scorekeeper_applications')
        .select('*')
        .eq('game_id', game.id);

      if (error) throw error;
      setScorekeeperApplications((data || []).map(app => ({
        ...app,
        backup_position: 1,
        scorekeeper_status: 'available'
      })) as ScorekeeperApplication[]);
    } catch (error) {
      console.error('Error loading scorekeeper applications:', error);
    }
  };

  const loadTeamLogos = async () => {
    if (!game) return;
    
    try {
      const teamIds = [game.home_team_id, game.away_team_id].filter(Boolean);
      if (teamIds.length === 0) return;

      const { data, error } = await supabase
        .from('team_logos')
        .select('team_id, logo_url')
        .in('team_id', teamIds)
        .eq('is_primary', true);

      if (error) throw error;

      const logoMap: { [key: number]: string } = {};
      data?.forEach(logo => {
        logoMap[logo.team_id] = logo.logo_url;
      });
      setTeamLogos(logoMap);
    } catch (error) {
      console.error('Error loading team logos:', error);
    }
  };

  const checkUserPermissions = async () => {
    if (!user || !game) return;

    try {
      // Check if user is a coach or team owner
      const { data: coachData } = await supabase
        .from('team_coaches')
        .select('team_id')
        .eq('coach_id', user.id);

      const { data: teamData } = await supabase
        .from('teams')
        .select('team_id')
        .eq('created_by', user.id);

      const userTeamIds = [
        ...(coachData?.map(c => c.team_id) || []),
        ...(teamData?.map(t => t.team_id) || [])
      ];

      const isInvolvedInGame = userTeamIds.includes(game.home_team_id) || 
                              userTeamIds.includes(game.away_team_id) ||
                              game.created_by === user.id;

      setIsCoachOrOwner(isInvolvedInGame);
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const handleRequestToJoin = async () => {
    if (!user || !game) {
      toast.error('Please log in to join games');
      return;
    }

    try {
      const { error } = await supabase
        .from('game_participants')
        .insert({
          game_id: game.id,
          player_id: user.id,
          player_type: supabaseUser ? 'supabase' : 'internal',
          team_side: 'home', // Default to home, coach can change
          participation_status: 'requested',
          eligibility_status: 'pending',
          requested_by: user.id
        });

      if (error) throw error;

      toast.success('Join request submitted successfully!');
      loadGameData();
    } catch (error) {
      console.error('Error requesting to join:', error);
      toast.error('Failed to submit join request');
    }
  };

  const handleApprovePlayer = async (participantId: string) => {
    try {
      const { error } = await supabase
        .from('game_participants')
        .update({
          participation_status: 'ready_to_play',
          eligibility_status: 'eligible',
          confirmed_by: user?.id,
          status_updated_at: new Date().toISOString()
        })
        .eq('id', participantId);

      if (error) throw error;

      toast.success('Player approved successfully!');
      loadGameData();
    } catch (error) {
      console.error('Error approving player:', error);
      toast.error('Failed to approve player');
    }
  };

  const handleRejectPlayer = async (participantId: string) => {
    try {
      const { error } = await supabase
        .from('game_participants')
        .update({
          participation_status: 'rejected',
          confirmed_by: user?.id,
          status_updated_at: new Date().toISOString()
        })
        .eq('id', participantId);

      if (error) throw error;

      toast.success('Player rejected');
      loadGameData();
    } catch (error) {
      console.error('Error rejecting player:', error);
      toast.error('Failed to reject player');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "destructive" | "outline" | "secondary"> = {
      requested: "outline",
      ready_to_play: "default",
      rejected: "destructive",
      no_show: "destructive",
      injured: "secondary"
    };

    return (
      <Badge variant={variants[status] || "outline"} className="capitalize">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getScorekeeperStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "destructive" | "outline" | "secondary"> = {
      pending: "outline",
      approved: "default",
      rejected: "destructive",
    };

    return (
      <Badge variant={variants[status] || "outline"} className="capitalize">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const formatGameTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      const formattedTime = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      return `${formattedTime} CST`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time;
    }
  };

  if (!game) {
    console.log('EnhancedGameDetailsModal: No game provided, modal hidden');
    return null;
  }

  console.log('EnhancedGameDetailsModal: Rendering with game:', game.game_name);

  const homeParticipants = participants.filter(p => p.team_side === 'home');
  const awayParticipants = participants.filter(p => p.team_side === 'away');
  const primaryScorekeeper = scorekeeperApplications.find(s => s.backup_position === 1 && s.status === 'approved');
  const backupScorekeepers = scorekeeperApplications.filter(s => s.backup_position > 1 && s.status === 'approved');
  const pendingScorekeepers = scorekeeperApplications.filter(s => s.status === 'pending');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            {game.game_name}
            <div className="flex gap-2">
              <Badge variant={game.game_status === 'confirmed' ? 'default' : 'outline'}>
                {game.game_status}
              </Badge>
              {(game as any).status && (game as any).status !== game.game_status && (
                <Badge variant={(game as any).status === 'approved' ? 'default' : 'destructive'}>
                  {(game as any).status}
                </Badge>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="overview" className="text-xs md:text-sm px-2 py-1">Overview</TabsTrigger>
            <TabsTrigger value="teams" className="text-xs md:text-sm px-2 py-1">Teams</TabsTrigger>
            <TabsTrigger value="scorekeepers" className="text-xs md:text-sm px-2 py-1">Scorekeepers</TabsTrigger>
            <TabsTrigger value="venue" className="text-xs md:text-sm px-2 py-1">Venue</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Game Info */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                    <Calendar className="h-5 w-5" />
                    Game Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{format(new Date(game.game_date), 'MMM d, yyyy')} at {formatGameTime(game.game_time)}</span>
                  </div>
                  {game.venue_name && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <div className="font-medium">{game.venue_name}</div>
                        {venueSection && (
                          <div className="text-sm text-muted-foreground">
                            Section: {venueSection.section_name} ({venueSection.section_type})
                            {venueSection.capacity && ` - Capacity: ${venueSection.capacity}`}
                          </div>
                        )}
                        {game.venue_address && (
                          <div className="text-sm text-muted-foreground">{game.venue_address}</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>Min {game.minimum_players_required} players required</span>
                  </div>
                  {game.game_code && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Game Code:</span>
                      <Badge variant="outline">{game.game_code}</Badge>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Matchup */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                    <Trophy className="h-5 w-5" />
                    Matchup
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 items-center">
                    <div className="text-center">
                      {teamLogos[game.home_team_id!] && (
                        <div className="w-16 h-16 mx-auto mb-2">
                          <TeamLogo 
                            logoUrl={teamLogos[game.home_team_id!]}
                            teamName={game.home_team?.team_name || game.home_team_label}
                            size="lg"
                          />
                        </div>
                      )}
                      <div className="font-medium text-foreground">{game.home_team?.team_name || game.home_team_label}</div>
                      <div className="text-sm text-muted-foreground">Home</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-muted-foreground">VS</div>
                    </div>
                    <div className="text-center">
                      {teamLogos[game.away_team_id!] && (
                        <div className="w-16 h-16 mx-auto mb-2">
                          <TeamLogo 
                            logoUrl={teamLogos[game.away_team_id!]}
                            teamName={game.away_team?.team_name || game.away_team_label}
                            size="lg"
                          />
                        </div>
                      )}
                      <div className="font-medium text-foreground">{game.away_team?.team_name || game.away_team_label}</div>
                      <div className="text-sm text-muted-foreground">Away</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Countdown Timer */}
            {game.countdown_end_at && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                    <Timer className="h-5 w-5" />
                    Roster Lock Countdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-sports-green">
                      {timeRemaining}
                    </div>
                    <div className="text-sm text-muted-foreground mt-2">
                      Until roster changes lock
                    </div>
                    {timeRemaining === 'LOCKED' && (
                      <div className="flex items-center justify-center gap-2 mt-2 text-destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <span>Roster locked</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="teams" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Home Team */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-foreground">
                    <span>{game.home_team?.team_name || game.home_team_label} (Home)</span>
                    <Badge variant="outline">{homeParticipants.length} players</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {homeParticipants.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No players registered</p>
                    ) : (
                      homeParticipants.map((participant) => (
                        <div key={participant.id} className="flex items-center justify-between p-2 border-border rounded bg-muted/50">
                          <div>
                            <div className="font-medium">
                              {participant.player_name}
                            </div>
                            {participant.position && (
                              <div className="text-sm text-muted-foreground">{participant.position}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(participant.participation_status)}
                            {isCoachOrOwner && participant.participation_status === 'requested' && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApprovePlayer(participant.id)}
                                  disabled={timeRemaining === 'LOCKED'}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRejectPlayer(participant.id)}
                                >
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Away Team */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-foreground">
                    <span>{game.away_team?.team_name || game.away_team_label} (Away)</span>
                    <Badge variant="outline">{awayParticipants.length} players</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {awayParticipants.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No players registered</p>
                    ) : (
                      awayParticipants.map((participant) => (
                        <div key={participant.id} className="flex items-center justify-between p-2 border-border rounded bg-muted/50">
                          <div>
                            <div className="font-medium">
                              {participant.player_name}
                            </div>
                            {participant.position && (
                              <div className="text-sm text-muted-foreground">{participant.position}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(participant.participation_status)}
                            {isCoachOrOwner && participant.participation_status === 'requested' && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApprovePlayer(participant.id)}
                                  disabled={timeRemaining === 'LOCKED'}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRejectPlayer(participant.id)}
                                >
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Join Actions */}
            <div className="flex gap-2 justify-center">
              <PlayerJoinRequestButton
                gameId={game.id}
                gameStatus={game.game_status}
                isAlreadyRequested={participants.some(p => p.player_id === user?.id)}
                isGameLocked={timeRemaining === 'LOCKED'}
                onRequestSuccess={loadGameData}
                homeTeamName={game.home_team?.team_name}
                awayTeamName={game.away_team?.team_name}
              />
            </div>
          </TabsContent>

          <TabsContent value="scorekeepers" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Scorekeeper Management</h3>
                {(() => {
                  const venueGame = game as any;
                  const isEligible = venueGame.status ? 
                    (venueGame.game_status === 'confirmed' && venueGame.status === 'approved') :
                    (game.game_status === 'confirmed');
                  
                  return isEligible ? (
                    <Button
                      onClick={() => setShowScorekeeperModal(true)}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-black font-medium"
                    >
                      <Star className="h-4 w-4" />
                      Apply as Scorekeeper
                    </Button>
                  ) : (
                    <div className="text-center">
                      <Button
                        disabled
                        variant="outline"
                        className="flex items-center gap-2 opacity-50 cursor-not-allowed"
                      >
                        <Star className="h-4 w-4" />
                        Apply as Scorekeeper
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">
                        {game.game_status !== 'confirmed' ? 'Game must be confirmed' : 
                         (game as any).status !== 'approved' ? 'Game must be approved by venue' :
                         'Applications only available for confirmed & approved games'}
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Primary Scorekeeper */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Primary Scorekeeper
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {primaryScorekeeper ? (
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <div className="font-medium">
                          {primaryScorekeeper.profiles ? 
                            `${primaryScorekeeper.profiles.first_name || ''} ${primaryScorekeeper.profiles.last_name || ''}`.trim() || 
                            primaryScorekeeper.profiles.username :
                            `Scorekeeper ${primaryScorekeeper.scorekeeper_id.slice(0, 8)}`
                          }
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Applied: {format(new Date(primaryScorekeeper.applied_at), 'MMM d, yyyy')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="default">Primary</Badge>
                        <Badge variant={primaryScorekeeper.scorekeeper_status === 'completed' ? 'default' : 'outline'}>
                          {primaryScorekeeper.scorekeeper_status}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No primary scorekeeper assigned</p>
                      <p className="text-sm">Applications will be reviewed by game organizers</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Backup Scorekeepers */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>Backup Scorekeepers ({backupScorekeepers.length}/3)</CardTitle>
                </CardHeader>
                <CardContent>
                  {backupScorekeepers.length > 0 ? (
                    <div className="space-y-2">
                      {backupScorekeepers.map((scorekeeper) => (
                        <div key={scorekeeper.id} className="flex items-center justify-between p-2 border-border rounded bg-muted/50">
                          <div>
                            <div className="font-medium">
                              {scorekeeper.profiles ? 
                                `${scorekeeper.profiles.first_name || ''} ${scorekeeper.profiles.last_name || ''}`.trim() || 
                                scorekeeper.profiles.username :
                                `Scorekeeper ${scorekeeper.scorekeeper_id.slice(0, 8)}`
                              }
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Backup #{scorekeeper.backup_position}
                            </div>
                          </div>
                          <Badge variant="outline">
                            {scorekeeper.scorekeeper_status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm text-center py-4">
                      No backup scorekeepers assigned
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Pending Applications */}
              {pendingScorekeepers.length > 0 && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle>Pending Applications ({pendingScorekeepers.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {pendingScorekeepers.map((scorekeeper) => (
                        <div key={scorekeeper.id} className="flex items-center justify-between p-2 border-border rounded bg-muted/50">
                          <div>
                            <div className="font-medium">
                              {scorekeeper.profiles ? 
                                `${scorekeeper.profiles.first_name || ''} ${scorekeeper.profiles.last_name || ''}`.trim() || 
                                scorekeeper.profiles.username :
                                `Scorekeeper ${scorekeeper.scorekeeper_id.slice(0, 8)}`
                              }
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Applied: {format(new Date(scorekeeper.applied_at), 'MMM d, yyyy')}
                            </div>
                          </div>
                          {getScorekeeperStatusBadge(scorekeeper.status)}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="venue" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Venue Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Location</h4>
                  <p className="text-foreground">{game.venue_name || 'Venue TBD'}</p>
                  {game.venue_address && (
                    <p className="text-sm text-muted-foreground">{game.venue_address}</p>
                  )}
                  {!game.venue_address && game.venue_name && game.venue_name !== 'Venue TBD' && (
                    <p className="text-sm text-muted-foreground">Address information not available</p>
                  )}
                </div>
                
                {venueSection && (
                  <div>
                    <h4 className="font-medium mb-2">Section Details</h4>
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{venueSection.section_name}</span>
                        <Badge variant="outline">{venueSection.section_type}</Badge>
                      </div>
                      {venueSection.capacity && (
                        <p className="text-sm text-muted-foreground">
                          Maximum Capacity: {venueSection.capacity} people
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-2">Game Requirements</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Minimum Players per Team:</span>
                      <span className="font-medium">{Math.ceil(game.minimum_players_required / 2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Players Required:</span>
                      <span className="font-medium">{game.minimum_players_required}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Current Home Players:</span>
                      <span className={`font-medium ${
                        homeParticipants.filter(p => p.participation_status === 'ready_to_play').length >= Math.ceil(game.minimum_players_required / 2) 
                          ? 'text-green-600' 
                          : 'text-orange-600'
                      }`}>
                        {homeParticipants.filter(p => p.participation_status === 'ready_to_play').length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Current Away Players:</span>
                      <span className={`font-medium ${
                        awayParticipants.filter(p => p.participation_status === 'ready_to_play').length >= Math.ceil(game.minimum_players_required / 2) 
                          ? 'text-green-600' 
                          : 'text-orange-600'
                      }`}>
                        {awayParticipants.filter(p => p.participation_status === 'ready_to_play').length}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <ScorekeeperApplicationModal
          game={game}
          isOpen={showScorekeeperModal}
          onClose={() => setShowScorekeeperModal(false)}
          onApplicationSubmitted={() => {
            loadScorekeeperApplications();
            setShowScorekeeperModal(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
